import { useQuery } from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import { graphql } from '@/gql';
import { GetBuildingFootprintsQuery } from '@/gql/graphql';
import projectKeys from './keyFactory';

const GetBuildingFootprints = graphql(`
  query GetBuildingFootprints($projectId: ID!, $location: SolarLatLngInput) {
    buildingFootprints(projectId: $projectId, location: $location) {
      osmId
      ring {
        lat
        lng
      }
    }
  }
`);

export type BuildingFootprint =
  GetBuildingFootprintsQuery['buildingFootprints'][number];

/**
 * Free OpenStreetMap building footprints near the project's site address, for
 * the solar designer's reference overlay and "trace a face from a real outline"
 * affordance. A best-effort aid: returns [] when nothing is mapped there or the
 * lookup fails.
 *
 * Building outlines are © OpenStreetMap contributors (ODbL) — render the
 * attribution wherever the footprints are shown.
 *
 * `location` recentres the lookup on an arbitrary point instead of the site
 * address — the confirm-location flow passes the settled pin position so the
 * outlines follow a drag beyond the original search radius. Keyed per point;
 * the pin only reports on drag-end so the key doesn't churn mid-drag.
 */
const useGetBuildingFootprints = (
  projectId?: string,
  options?: { enabled?: boolean; location?: { lat: number; lng: number } }
) => {
  const location = options?.location;
  const { data, ...rest } = useQuery({
    enabled: !!projectId && (options?.enabled ?? true),
    queryKey: projectKeys.buildingFootprints(projectId!, location),
    queryFn: () =>
      gqlClient.request(GetBuildingFootprints, {
        projectId: projectId!,
        location,
      }),
    // OSM footprints are stable for a site — cache generously within a session.
    staleTime: 1000 * 60 * 30,
  });
  return {
    footprints: data?.buildingFootprints ?? [],
    ...rest,
  };
};

export default useGetBuildingFootprints;
